import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { HelpButton } from '@/components/HelpButton';
import { EmptyState } from '@/components/EmptyState';
import { PageHint } from '@/components/HelpText';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import { createInstitution, listInstitutions, type Institution } from '@/lib/finance';

const TYPES: { value: Institution['type']; label: string }[] = [
  { value: 'bank', label: 'Banco' },
  { value: 'broker', label: 'Corretora' },
  { value: 'fintech', label: 'Fintech' },
  { value: 'other', label: 'Outro' },
];

export function InstitutionsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['institutions'],
    queryFn: listInstitutions,
  });
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Instituições</h1>
          <HelpButton topic="instituicoes" />
        </div>
        <button
          onClick={() => setOpen(true)}
          className="tap rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white"
        >
          + nova
        </button>
      </header>
      <PageHint>
        Instituições são <strong>opcionais</strong> e valem para a <strong>casa toda</strong> —
        cadastre cada banco <strong>uma única vez</strong> (não crie "Inter" e "Inter Ari"; quem
        diz de quem é a conta é a própria conta). Não interferem em saldo nem em métodos.
      </PageHint>
      {isLoading && <p className="text-slate-500">carregando…</p>}
      {data?.length === 0 && (
        <EmptyState title="Sem instituições" icon="🏢" />
      )}
      {(data?.length ?? 0) > 0 && (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white text-sm shadow-sm">
          {data?.map((i) => (
            <li key={i.id} className="flex items-center justify-between px-4 py-3">
              <span className="min-w-0">
                <span className="block truncate font-medium">{i.name}</span>
                <span className="text-xs text-slate-500">
                  {TYPES.find((t) => t.value === i.type)?.label ?? i.type}
                </span>
              </span>
              {i.is_system && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                  sistema
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <CreateInstModal
        open={open}
        onClose={() => setOpen(false)}
        existing={data ?? []}
        onCreated={() => {
          setOpen(false);
          toast.success('instituição criada');
          qc.invalidateQueries({ queryKey: ['institutions'] });
        }}
      />
    </div>
  );
}

/** Normaliza pra detectar duplicatas: minúsculas, sem acentos e sem sufixos de pessoa. */
function normalizeInstName(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function CreateInstModal({
  open,
  onClose,
  onCreated,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  existing: Institution[];
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<Institution['type']>('bank');
  const [error, setError] = useState<string | null>(null);

  const typed = normalizeInstName(name);
  const similar =
    typed.length >= 3
      ? existing.find((i) => {
          const n = normalizeInstName(i.name);
          return n === typed || n.startsWith(typed) || typed.startsWith(n);
        })
      : undefined;

  const mutation = useMutation({
    mutationFn: createInstitution,
    onSuccess: () => {
      setName('');
      setType('bank');
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ name, type });
  }

  return (
    <Modal open={open} title="Nova instituição" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Nome</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Itaú, Nubank, XP"
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </label>
        {similar && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
            ⚠️ Já existe <strong>"{similar.name}"</strong> — instituições valem para a casa toda,
            não precisa criar uma por pessoa. Crie só se for realmente outro banco.
          </p>
        )}
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Tipo</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as Institution['type'])}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        {error && <p className="text-red-600">{error}</p>}
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'criando…' : 'criar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
